import React, { useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Tooltip,
  Badge,
  Paper
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { MaintenanceImage } from '../../utils/types';

interface MaintenanceImageGalleryProps {
  images: MaintenanceImage[];
  onDelete?: (imageId: string) => void;
  onShare?: (imageUrl: string) => void;
  readOnly?: boolean;
  title?: string;
}

const MaintenanceImageGallery: React.FC<MaintenanceImageGalleryProps> = ({
  images,
  onDelete,
  onShare,
  readOnly = false,
  title = "صور الجهاز"
}) => {
  const [selectedImage, setSelectedImage] = useState<MaintenanceImage | null>(null);
  const [imageViewOpen, setImageViewOpen] = useState(false);

  const typeLabels = {
    serial: 'صورة الرقم التسلسلي والموديل',
    model: 'صورة العميل عند الاستلام',
    defect: 'صورة العيب أو المشكلة'
  };

  const typeColors = {
    serial: 'primary',
    model: 'secondary',
    defect: 'error'
  } as const;

  const typeIcons = {
    serial: '📱',
    model: '👤',
    defect: '⚠️'
  };

  // فتح صورة للعرض
  const handleImageClick = (image: MaintenanceImage) => {
    setSelectedImage(image);
    setImageViewOpen(true);
  };

  // إغلاق عارض الصور
  const handleCloseImageView = () => {
    setSelectedImage(null);
    setImageViewOpen(false);
  };

  // تحويل timestamp إلى Date object بشكل آمن مع معالجة جميع الحالات
  const getTimestampAsDate = (timestamp: Date | string | any): Date => {
    try {
      // تشخيص نوع البيانات
      console.log('Processing timestamp:', {
        value: timestamp,
        type: typeof timestamp,
        isDate: timestamp instanceof Date,
        hasToDate: timestamp && typeof timestamp.toDate === 'function'
      });

      // إذا كان null أو undefined، استخدم التاريخ الحالي
      if (!timestamp) {
        console.log('Timestamp is null/undefined, using current date');
        return new Date();
      }

      // إذا كان كائن Date صحيح
      if (timestamp instanceof Date && !isNaN(timestamp.getTime())) {
        console.log('Valid Date object found');
        return timestamp;
      }

      // إذا كان نص، حول إلى Date
      if (typeof timestamp === 'string') {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          console.log('Successfully converted string to Date');
          return date;
        }
      }

      // إذا كان كائن Firestore Timestamp
      if (timestamp && typeof timestamp.toDate === 'function') {
        console.log('Firestore Timestamp detected');
        return timestamp.toDate();
      }

      // إذا كان رقم (Unix timestamp)
      if (typeof timestamp === 'number') {
        console.log('Number timestamp detected');
        return new Date(timestamp);
      }

      // في حالة فشل كل شيء، استخدم التاريخ الحالي
      console.warn('Invalid timestamp format, using current date:', timestamp);
      return new Date();
    } catch (error) {
      console.error('Error converting timestamp, using current date:', error, timestamp);
      return new Date();
    }
  };

  // دالة آمنة لتنسيق التاريخ
  const formatDate = (timestamp: Date | string | any): string => {
    try {
      const date = getTimestampAsDate(timestamp);
      return date.toLocaleDateString('ar-EG');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'تاريخ غير صحيح';
    }
  };

  // دالة آمنة لتنسيق الوقت
  const formatTime = (timestamp: Date | string | any): string => {
    try {
      const date = getTimestampAsDate(timestamp);
      return date.toLocaleTimeString('ar-EG');
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'وقت غير صحيح';
    }
  };

  // دالة آمنة لتنسيق التاريخ والوقت معاً
  const formatDateTime = (timestamp: Date | string | any): string => {
    try {
      const date = getTimestampAsDate(timestamp);
      return date.toLocaleString('ar-EG');
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return 'تاريخ ووقت غير صحيح';
    }
  };

  // تحميل الصورة
  const handleDownload = (image: MaintenanceImage) => {
    const link = document.createElement('a');
    link.href = image.uploadedUrl || image.url;
    const timestamp = getTimestampAsDate(image.timestamp);
    link.download = `${image.type}_${timestamp.getTime()}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // مشاركة الصورة
  const handleShare = (image: MaintenanceImage) => {
    if (onShare) {
      onShare(image.uploadedUrl || image.url);
    } else if (navigator.share) {
      navigator.share({
        title: `صورة ${image.label}`,
        url: image.uploadedUrl || image.url
      });
    } else {
      // نسخ الرابط إلى الحافظة
      navigator.clipboard.writeText(image.uploadedUrl || image.url);
    }
  };

  // حذف الصورة
  const handleDelete = (image: MaintenanceImage) => {
    if (onDelete) {
      onDelete(image.id);
    }
  };

  // فتح الصورة في تبويب جديد
  const handleOpenInNew = (image: MaintenanceImage) => {
    window.open(image.uploadedUrl || image.url, '_blank');
  };

  if (images.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
        <PhotoCameraIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          لا توجد صور
        </Typography>
        <Typography variant="body2" color="text.secondary">
          لم يتم رفع أي صور لهذا الجهاز بعد
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PhotoCameraIcon color="primary" />
        {title}
        <Badge badgeContent={images.length} color="primary" />
      </Typography>

      <Grid container spacing={2}>
        {images.map((image) => (
          <Grid item xs={12} sm={6} md={4} key={image.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={image.uploadedUrl || image.url}
                  alt={image.label}
                  onClick={() => handleImageClick(image)}
                  sx={{ 
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                />
                
                {/* أيقونة التكبير */}
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                  size="small"
                  onClick={() => handleImageClick(image)}
                >
                  <ZoomInIcon />
                </IconButton>

                {/* شريحة نوع الصورة */}
                <Chip
                  label={`${typeIcons[image.type]} ${typeLabels[image.type]}`}
                  color={typeColors[image.type]}
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    fontWeight: 'bold'
                  }}
                />
              </Box>

              <CardContent sx={{ p: 2 }}>
                <Box display="flex" justifyContent="between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(image.timestamp)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(image.timestamp)}
                    </Typography>
                    {image.provider && (
                      <Chip
                        label={image.provider.toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>

                  <Box display="flex" gap={0.5}>
                    <Tooltip title="تحميل">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(image)}
                        color="primary"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="مشاركة">
                      <IconButton
                        size="small"
                        onClick={() => handleShare(image)}
                        color="secondary"
                      >
                        <ShareIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="فتح في تبويب جديد">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenInNew(image)}
                        color="info"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {!readOnly && onDelete && (
                      <Tooltip title="حذف">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(image)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* عارض الصور */}
      <Dialog
        open={imageViewOpen}
        onClose={handleCloseImageView}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { bgcolor: 'black' }
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'between' }}>
          <Box display="flex" alignItems="center" gap={1}>
            {selectedImage && (
              <>
                <Typography variant="h6">
                  {typeIcons[selectedImage.type]} {typeLabels[selectedImage.type]}
                </Typography>
                <Chip
                  label={selectedImage.provider?.toUpperCase() || 'محلي'}
                  size="small"
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'white' }}
                />
              </>
            )}
          </Box>
          <IconButton onClick={handleCloseImageView} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage.uploadedUrl || selectedImage.url}
              alt={selectedImage.label}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: 'black', color: 'white' }}>
          {selectedImage && (
            <>
              <Typography variant="body2" sx={{ color: 'white', mr: 'auto' }}>
                تم التقاطها في: {formatDateTime(selectedImage.timestamp)}
              </Typography>
              
              <Button
                onClick={() => handleDownload(selectedImage)}
                startIcon={<DownloadIcon />}
                sx={{ color: 'white' }}
              >
                تحميل
              </Button>
              
              <Button
                onClick={() => handleShare(selectedImage)}
                startIcon={<ShareIcon />}
                sx={{ color: 'white' }}
              >
                مشاركة
              </Button>
              
              <Button
                onClick={() => handleOpenInNew(selectedImage)}
                startIcon={<OpenInNewIcon />}
                sx={{ color: 'white' }}
              >
                فتح في تبويب جديد
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceImageGallery;
